/**
 * CollisionDebugger
 * Specialized debugger for collision events with real-time event streaming
 */

import { PhysicsLogger, CollisionEvent } from '../logging/PhysicsLogger';
import { PhysicsEntity, Platform, Helix2000Character } from '../../shared/CharacterTypes';
import { gameLogger } from '../../utils/GameLogger';
import { Bounds } from '../systems/physics/types/CollisionTypes';

// Re-export CollisionEvent for convenience
export type { CollisionEvent } from '../logging/PhysicsLogger';

export enum DebugVerbosity {
    NONE = 0,      // No debug logging
    ERRORS = 1,    // Log errors only
    OCCASIONAL = 2, // Log occasional collisions (1% chance)
    ALL = 3        // Log all collisions
}

export interface CollisionStatistics {
  eventsPerSecond: number;
  totalEvents: number;
  eventsByType: Map<string, number>;
  lastEventTime: number;
}

export interface CollisionDebugData {
  event: CollisionEvent;
  playerBounds?: { left: number; right: number; top: number; bottom: number };
  platformBounds?: { left: number; right: number; top: number; bottom: number };
  overlap?: { x: number; y: number };
}

export class CollisionDebugger {
  private static instance: CollisionDebugger;
  private collisionBuffer: CollisionDebugData[] = [];
  private readonly bufferSize = 50;
  private eventTimestamps: number[] = [];
  /** First timestamp still inside the 5 s window; older entries are dropped lazily. */
  private eventHead = 0;
  /** eventTimestamps (from eventHead) is non-decreasing — false only after the clock steps back. */
  private eventsInOrder = true;
  private statistics: CollisionStatistics;
  private physicsLogger: PhysicsLogger;

  // Throttle state
  private verbosity: DebugVerbosity = DebugVerbosity.ERRORS;
  private logProbability: number = 0.01;
  private lastLogTime: number = 0;
  private readonly LOG_COOLDOWN_MS = 1000;

  // Event subscription callbacks
  private eventCallbacks: Array<(data: CollisionDebugData) => void> = [];

  private constructor() {
    this.physicsLogger = PhysicsLogger.getInstance();
    this.statistics = {
      eventsPerSecond: 0,
      totalEvents: 0,
      eventsByType: new Map(),
      lastEventTime: 0
    };
  }

  public static getInstance(): CollisionDebugger {
    if (!CollisionDebugger.instance) {
      CollisionDebugger.instance = new CollisionDebugger();
    }
    return CollisionDebugger.instance;
  }

  /**
   * Record a collision event with detailed debug information
   */
  public recordCollision(
    event: CollisionEvent,
    playerBounds?: any,
    platformBounds?: any,
    overlap?: any
  ): void {
    const debugData: CollisionDebugData = {
      event,
      playerBounds,
      platformBounds,
      overlap
    };

    // Add to circular buffer
    this.collisionBuffer.push(debugData);
    if (this.collisionBuffer.length > this.bufferSize) {
      this.collisionBuffer.shift();
    }

    // Update statistics
    this.updateStatistics(event);

    // Log through PhysicsLogger
    this.physicsLogger.logCollision(event);

    // Notify subscribers
    this.eventCallbacks.forEach(callback => callback(debugData));
  }

  /**
   * Subscribe to collision events
   */
  public subscribe(callback: (data: CollisionDebugData) => void): () => void {
    this.eventCallbacks.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.eventCallbacks.indexOf(callback);
      if (index > -1) {
        this.eventCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Update collision statistics
   */
  private updateStatistics(event: CollisionEvent): void {
    const now = Date.now();
    this.cleanupTimestamps(now);
    const timestamps = this.eventTimestamps;
    if (timestamps.length > this.eventHead && now < timestamps[timestamps.length - 1]) this.eventsInOrder = false;
    timestamps.push(now);
    this.statistics.totalEvents++;
    this.statistics.lastEventTime = now;

    // Count by type
    const typeCount = this.statistics.eventsByType.get(event.type) || 0;
    this.statistics.eventsByType.set(event.type, typeCount + 1);

    // Calculate events per second (last 1000ms)
    this.statistics.eventsPerSecond = this.countWithin(now, 1000);
  }

  /**
   * Keep the last 5 seconds: the same entries `filter(ts => now - ts < 5000)` keeps. Ordered
   * timestamps expire from the front, so this walks a head index instead of copying the array on
   * every collision (2026-10-23 gate profile); after a clock step back it filters as before.
   */
  private cleanupTimestamps(now: number = Date.now()): void {
    if (!this.eventsInOrder) {
      this.eventTimestamps = this.eventTimestamps.slice(this.eventHead).filter(ts => now - ts < 5000);
      this.eventHead = 0;
      this.eventsInOrder = this.eventTimestamps.every((ts, i, all) => i === 0 || all[i - 1] <= ts);
      return;
    }
    const timestamps = this.eventTimestamps;
    while (this.eventHead < timestamps.length && !(now - timestamps[this.eventHead] < 5000)) this.eventHead++;
    if (this.eventHead >= 1024 && this.eventHead * 2 >= timestamps.length) {
      timestamps.splice(0, this.eventHead);
      this.eventHead = 0;
    }
  }

  /** How many kept timestamps satisfy `now - ts < windowMs` (binary search when ordered). */
  private countWithin(now: number, windowMs: number): number {
    const timestamps = this.eventTimestamps;
    if (!this.eventsInOrder) {
      let count = 0;
      for (let i = this.eventHead; i < timestamps.length; i++) if (now - timestamps[i] < windowMs) count++;
      return count;
    }
    let lo = this.eventHead, hi = timestamps.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (now - timestamps[mid] < windowMs) hi = mid; else lo = mid + 1;
    }
    return timestamps.length - lo;
  }

  /**
   * Get recent collision events
   */
  public getRecentCollisions(count: number = 10): CollisionDebugData[] {
    return this.collisionBuffer.slice(-count);
  }

  /**
   * Get collision statistics
   */
  public getStatistics(): CollisionStatistics {
    const now = Date.now();
    this.cleanupTimestamps(now);
    this.statistics.eventsPerSecond = this.countWithin(now, 1000);
    return {
      ...this.statistics,
      eventsByType: new Map(this.statistics.eventsByType),
    };
  }

  /**
   * Clear collision buffer
   */
  public clearBuffer(): void {
    this.collisionBuffer = [];
    this.eventTimestamps = [];
    this.eventHead = 0;
    this.eventsInOrder = true;
    this.statistics = {
      eventsPerSecond: 0,
      totalEvents: 0,
      eventsByType: new Map(),
      lastEventTime: 0
    };
    this.physicsLogger.clearCollisionBuffer();
  }

  /**
   * Check if collision is occurring at position
   */
  public checkCollisionAt(
    x: number,
    y: number,
    _character: Helix2000Character,
    platforms: Platform[]
  ): boolean {
    const charBounds = {
      left: x,
      right: x + 40,
      top: y,
      bottom: y + 40
    };

    return platforms.some(platform => {
      const platformBounds = {
        left: platform.x,
        right: platform.x + platform.width,
        top: platform.y,
        bottom: platform.y + platform.height
      };

      return this.isColliding(charBounds, platformBounds);
    });
  }

  /**
   * Check if two bounds are colliding
   */
  private isColliding(bounds1: any, bounds2: any): boolean {
    return bounds1.left < bounds2.right &&
           bounds1.right > bounds2.left &&
           bounds1.top < bounds2.bottom &&
           bounds1.bottom > bounds2.top;
  }

  /**
   * Set debug verbosity level
   */
  public setVerbosity(verbosity: DebugVerbosity): void {
    this.verbosity = verbosity;
    gameLogger.info('physics', `🔧 Physics collision debug verbosity set to: ${DebugVerbosity[verbosity]}`);
  }

  /**
   * Set log probability for OCCASIONAL level
   */
  public setLogProbability(probability: number): void {
    this.logProbability = Math.max(0, Math.min(1, probability));
    gameLogger.info('physics', `🔧 Physics collision log probability set to: ${(probability * 100).toFixed(1)}%`);
  }

  /**
   * Log collision details based on verbosity settings
   */
  public logCollision(
    charBounds: Bounds,
    platformBounds: Bounds,
    character: PhysicsEntity,
    platform: Platform,
    overlapX?: number,
    overlapY?: number
  ): void {
    if (this.verbosity === DebugVerbosity.NONE) {
      return;
    }

    // Time-based throttling - max 1 log per second per character
    const now = Date.now();
    if (now - this.lastLogTime < this.LOG_COOLDOWN_MS) {
      return;
    }

    if (this.verbosity === DebugVerbosity.ERRORS) {
      // G.4 (2026-05-13) + H.3 (2026-05-13): only log as ERROR when the
      // SMALLER axis exceeds half the player size (i.e. the player is
      // inside the platform on BOTH axes by more than 20px). Wide
      // platforms (ground 1920px wide) routinely produce overlapX in
      // the hundreds when the player stands on them, with overlapY
      // ≈ 7-10px from normal gravity-sink. Pre-H.3 the criterion
      // `maxOverlap > 60` flagged those as tunneling — false positives.
      // True tunneling requires the player to be deep on BOTH axes; the
      // resolver correctly handles single-axis overlap (lands/walls).
      // Player is 40×40, so min-overlap > 20 means the player center
      // (or center-line) is INSIDE the platform — that's tunneling.
      if (overlapX !== undefined && overlapY !== undefined && overlapX > 0 && overlapY > 0) {
        const minOverlap = Math.min(overlapX, overlapY);
        const isRealTunneling = minOverlap > 20;
        if (isRealTunneling) {
          this.lastLogTime = now;
          this.logErrorCollision(charBounds, platformBounds, character, platform, overlapX, overlapY);
        }
      }
      return;
    }

    if (this.verbosity === DebugVerbosity.OCCASIONAL) {
      if (Math.random() < this.logProbability) {
        this.lastLogTime = now;
        this.logCollisionDetails(charBounds, platformBounds, character, platform, overlapX, overlapY);
      }
      return;
    }

    this.lastLogTime = now;
    this.logCollisionDetails(charBounds, platformBounds, character, platform, overlapX, overlapY);
  }

  private logErrorCollision(
    charBounds: Bounds,
    platformBounds: Bounds,
    character: PhysicsEntity,
    platform: Platform,
    overlapX: number,
    overlapY: number
  ): void {
    gameLogger.error('physics', '❌ COLLISION ERROR - Large overlap detected:');
    gameLogger.error('physics', `   Player: (${charBounds.left.toFixed(1)}, ${charBounds.top.toFixed(1)}) → (${charBounds.right.toFixed(1)}, ${charBounds.bottom.toFixed(1)})`);
    gameLogger.error('physics', `   Platform: (${platformBounds.left.toFixed(1)}, ${platformBounds.top.toFixed(1)}) → (${platformBounds.right.toFixed(1)}, ${platformBounds.bottom.toFixed(1)})`);
    gameLogger.error('physics', `   Overlap: X=${overlapX.toFixed(1)}, Y=${overlapY.toFixed(1)}`);
    gameLogger.error('physics', `   Player pos: (${character.position.x.toFixed(1)}, ${character.position.y.toFixed(1)})`);
    gameLogger.error('physics', `   Platform: ${platform.id} at (${platform.x}, ${platform.y}) type=${platform.type}`);
  }

  private logCollisionDetails(
    charBounds: Bounds,
    platformBounds: Bounds,
    character: PhysicsEntity,
    platform: Platform,
    overlapX?: number,
    overlapY?: number
  ): void {
    gameLogger.info('physics', '🔍 COLLISION DEBUG:');
    gameLogger.info('physics', `   Player: (${charBounds.left.toFixed(1)}, ${charBounds.top.toFixed(1)}) → (${charBounds.right.toFixed(1)}, ${charBounds.bottom.toFixed(1)})`);
    gameLogger.info('physics', `   Platform: (${platformBounds.left.toFixed(1)}, ${platformBounds.top.toFixed(1)}) → (${platformBounds.right.toFixed(1)}, ${platformBounds.bottom.toFixed(1)})`);
    if (overlapX !== undefined && overlapY !== undefined) {
      gameLogger.info('physics', `   Overlap: X=${overlapX.toFixed(1)}, Y=${overlapY.toFixed(1)}`);
    }
    gameLogger.info('physics', `   Player pos: (${character.position.x.toFixed(1)}, ${character.position.y.toFixed(1)})`);
    gameLogger.info('physics', `   Platform: ${platform.id} at (${platform.x}, ${platform.y}) type=${platform.type}`);
    gameLogger.info('physics', `   Player grounded: ${character.isGrounded}, velocity: (${character.velocity.x.toFixed(1)}, ${character.velocity.y.toFixed(1)})`);
  }

}

// Export singleton instance and convenience functions
export const collisionDebugger = CollisionDebugger.getInstance();

export const recordCollision = (
  event: CollisionEvent,
  playerBounds?: any,
  platformBounds?: any,
  overlap?: any
) => {
  collisionDebugger.recordCollision(event, playerBounds, platformBounds, overlap);
};

export const subscribeToCollisions = (callback: (data: CollisionDebugData) => void) => {
  return collisionDebugger.subscribe(callback);
};

export const getCollisionStatistics = () => {
  return collisionDebugger.getStatistics();
};

export const clearCollisionDebugger = () => {
  collisionDebugger.clearBuffer();
};
